import { COLORS, hexToPhaser } from "./colors";

/**
 * Bad Connection Indicator UI
 * Displays a warning symbol at the center-top of the screen when connection is poor
 */
export class BadConnectionIndicator {
  private scene: Phaser.Scene;
  private container?: Phaser.GameObjects.Container;
  private wifiIcon?: Phaser.GameObjects.Image;
  private warningText?: Phaser.GameObjects.Text;
  private isVisible: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Create the bad connection indicator UI
   */
  public create(): void {
    if (this.container) return; // Already created

    // Create container for the indicator
    this.container = this.scene.add.container(
      this.scene.scale.width / 2,
      this.scene.scale.height / 2
    );
    this.container.setDepth(1000);
    this.container.setVisible(false);

    // Create semi-transparent background panel
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.RED), 0.6);
    panelBg.fillRoundedRect(-100, -30, 200, 60, 8);
    this.container.add(panelBg);

    // Create warning icon (broken WiFi signal)
    this.wifiIcon = this.scene.add.image(0, 0, "no-wifi-icon");
    this.wifiIcon.setScale(0.15);
    this.wifiIcon.setPosition(-80, 0);
    this.container.add(this.wifiIcon);

    // Create text
    this.warningText = this.scene.add
      .text(0, 0, "Bad Connection", {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0.5)
      .setPosition(20, 0);
    this.container.add(this.warningText);
  }

  /**
   * Show the bad connection indicator with optional animation
   */
  public show(): void {
    if (this.isVisible || !this.container) return;

    this.isVisible = true;
    this.container.setVisible(true);

    // Add pulse animation
    this.scene.tweens.add({
      targets: this.container,
      alpha: [0.7, 1, 0.7],
      duration: 1500,
      repeat: -1,
      ease: "Sine.inOut",
    });
  }

  /**
   * Hide the bad connection indicator
   */
  public hide(): void {
    if (!this.isVisible || !this.container) return;

    this.isVisible = false;
    this.container.setVisible(false);

    // Stop all tweens
    this.scene.tweens.killTweensOf(this.container);
  }

  /**
   * Check if the indicator is visible
   */
  public getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Destroy the indicator (cleanup)
   */
  public destroy(): void {
    if (this.container) {
      this.scene.tweens.killTweensOf(this.container);
      this.container.destroy();
      this.container = undefined;
    }
    this.wifiIcon = undefined;
    this.warningText = undefined;
  }
}
